import { createFileRoute } from '@tanstack/react-router'
import { ComparisonPage, comparisonScripts, type ComparisonData } from '@/components/comparison-page'

export const Route = createFileRoute('/_site/compare/statping')({
  component: () => <ComparisonPage data={data} />,
  head: () => ({
    meta: [
      { title: 'Statping-ng Alternative — Uptrack Uptime Monitoring' },
      { name: 'description', content: "Uptrack vs Statping-ng: a self-hosted status page and monitor vs a hosted service. Compare setup, check types, alerts, and upkeep." },
      { property: 'og:title', content: 'Statping-ng Alternative — Uptrack' },
      { property: 'og:description', content: "Statping-ng is free and self-hosted; its latest release is v0.93.0 from June 2025. Uptrack is hosted, from $3/mo." },
      { property: 'og:image', content: 'https://uptrack.app/og/compare-statping.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/compare/statping' },
    ],
    scripts: comparisonScripts('statping', "Statping-ng"),
  }),
})

const data: ComparisonData = {
  competitor: "Statping-ng",
  tagline: "Statping-ng is an open-source, self-hosted status page and monitor with themeable pages and broad protocol support. Uptrack is a hosted alternative, from $3/mo.",
  heroDescription: "These facts come from the Statping-ng GitHub repository as of October 2026, and from Uptrack's pricing page.",

  features: [
    { feature: "Pricing", uptrack: "Free plan (5 monitors); Pro from $3/mo", competitor: "Free and open source (GPL-3.0)" },
    { feature: "Latest release", uptrack: "Hosted service, updated continuously", competitor: "v0.93.0 (June 2025)", uptrackWins: true },
    { feature: "Infrastructure required", uptrack: "None (hosted)", competitor: "Your own server", uptrackWins: true },
    { feature: "Check types", uptrack: "HTTP, keyword, TCP, ping, DNS, SSL, domain expiry, heartbeat, browser (Playwright)", competitor: "HTTP, TCP, UDP, gRPC, ICMP" },
    { feature: "Status page themes", uptrack: "Default theme; theming on Team and Business", competitor: "Customizable SASS themes" },
    { feature: "gRPC monitoring", uptrack: "Not available", competitor: "Supported" },
    { feature: "Plugin framework", uptrack: "REST API + webhooks", competitor: "Built-in plugin system (Go)" },
    { feature: "Database backends", uptrack: "Managed (PostgreSQL)", competitor: "SQLite, PostgreSQL, MySQL" },
  ],

  painPoints: [
    {
      title: "Release activity",
      description: "The latest Statping-ng release is v0.93.0 from June 2025, and the repository had no new commits after that as of October 2026. Check the repository before you rely on it for production monitoring.",
    },
    {
      title: "Self-hosting upkeep",
      description: "You run the server, updates, backups, and TLS. If that server goes down, its checks and alerts stop too.",
    },
    {
      title: "Pre-1.0 versioning",
      description: "Statping-ng is still versioned below 1.0. Read the release notes before each upgrade, especially for database changes.",
    },
  ],

  differentiators: [
    "Nothing to host: no servers, containers, or backups to maintain. Uptrack runs on redundant, multi-node infrastructure.",
    "Alert confirmation with 2-5 consecutive failed checks before alerting; several check locations must confirm.",
    "Browser checks: Pro includes 5 Playwright monitors that run flows like login or checkout and save a screenshot at each step.",
    "Team members with roles and permissions, included in every paid plan.",
    "REST API and an MCP server, so you can manage monitors from scripts or AI assistants such as Claude.",
  ],

  notForYou: [
    "If you need gRPC monitoring, Statping-ng has native gRPC health checks. Uptrack doesn't.",
    "If you want deeply customizable status page themes with SASS, Statping-ng's theming is more flexible than Uptrack's.",
    "If you want a plugin framework to extend monitoring with custom Go code, Statping-ng's plugin system allows deep customization.",
    "If you need UDP monitoring, Statping-ng supports it natively. Uptrack doesn't check UDP.",
    "If you're comfortable maintaining the software yourself, Statping-ng still works and is free to run.",
    "If you need to choose your own database backend (SQLite for small setups, MySQL for existing infrastructure), Statping-ng offers that flexibility.",
  ],
}
